import {Injectable} from '@angular/core';
import {FormatType, NumberDisplayType} from '../models/chart';
import { UnitType } from '../models/unit.type';

@Injectable()
export class NumericFormatUtilsService {
    private units = {
        k: 1000,
        kilo: 1000,
        m: 1000000,
        million: 1000000,
        b: 1000000000,
        billion: 1000000000,
        trillion: 1000000000000,
        auto: (maxValue: number, data: number) => {
            if(maxValue == null || maxValue < 1000) {
                return data;
            }

            if(maxValue < 1000000) {
                return data / 1000;
            }

            if(maxValue < 1000000000) {
                return data / 1000000;
            }

            if(maxValue < 1000000000000) {
                return data / 1000000000;
            }

            return data / 1000000000000;
        },
    };

    private unitSigns = {
        k: 'K',
        kilo: 'K',
        m: 'M',
        million: 'M',
        b: 'B',
        billion: 'B',
        trillion: 'T',
        auto: (maxValue: number) => {
            if(maxValue == null || maxValue < 1000) {
                return 'None';
            }

            if(maxValue < 1000000) {
                return 'K';
            }

            if(maxValue < 1000000000) {
                return 'M';
            }

            if(maxValue < 1000000000000) {
                return 'B';
            }

            return 'T';
        },
    };

    public formatUnit(data: string | number, unit: string | UnitType, maxValue: number = undefined): number| string {
        const divider = this.units[unit?.toLowerCase()];
        if(divider == null) {
            return data;
        }
        if(typeof divider === 'number') {
            return Number(data) / divider;
        }
        return divider(maxValue < 0 ? (maxValue * -1) : maxValue , Number(data));
    }

    public addUnitSign(data: string | number, unit: string , maxValue): string | number {
        const unitSign = this.getUnitSign(unit, maxValue);
        if (unitSign == null ||
            unitSign === '' ||
            unitSign === 'بدون مقیاس' ||
            unitSign === 'ﺑﺪون واﺣﺪ' ||
            unitSign === 'بدون واحد' ||
            unitSign === 'None') {
            return data;
        }
        return `${data}${unitSign}`;
    }

    public languageEncode(numbersDisplayType: NumberDisplayType, value: string | number): string {
        const data = value?.toString()?.split('');
        if(data !== undefined && (numbersDisplayType === undefined || numbersDisplayType === 'fa')) {
            let formatData = '';
            data.forEach(d => {
                formatData += this.getPersianNumber(d);
            });
            return formatData;
        }
        return value?.toString();
    }
    
    public formatDecimalPoint(data: string | number, decimalPointNumber: number,
        autoToFixed?: boolean): string {
        if(!isNaN(+data) && decimalPointNumber != null) {
            const res = Number(data).toFixed(decimalPointNumber);
            if(autoToFixed) {
                return Number(res).toString();
            }
            return res;
        }
        return data.toString();
    }
    public formatSeparator(data: string, hasSeparator: boolean): string {
        if (hasSeparator) {
            const parts = data.toString().split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return parts.join('.');
        } else {
            return data;
        }
    }

    public formatSign(data: string, hasDollarSign: boolean, hasPercentSign: boolean, hasSeparator: boolean): string {
        // separator causes the number to appear as string so we check if theres a separator
        if(hasSeparator){
            const valueWithoutSeparator = data.replaceAll(',', '');
            if(!isNaN(+valueWithoutSeparator)){
                if (hasDollarSign) {
                    return `$${data}`;
                } else if (hasPercentSign) {
                    return `${data}%`;
                } else {
                    return data;
                }
            } else {
                return data;
            }
        } else {
            if(isNaN(+data)){
                return data;
            } else {
                if (hasDollarSign) {
                    return `$${data}`;
                } else if (hasPercentSign) {
                    return `${data}%`;
                } else {
                    return data;
                }
            }
        }
    }

    public formatMinus(data: string, formatType: FormatType, negativeNumberFormat: string): string {
        if (formatType === 'Currency' || formatType === 'Number') {
            if (negativeNumberFormat === 'ParenthesesSign' && data.substring(0, 1) === '-') {
                return data.replace('-', '(') + ')';
            } else if (negativeNumberFormat === 'RedColor' && data.substring(0, 1) === '-') {
                return data.replace('-', '');
            } else if (data.substring(0, 1) === '-') {
                return data.replace('-', '') + '-';
            }
        } else if (data.substring(0, 1) === '-') {
            return data.replace('-', '') + '-';
        }
        return data;
    }

    public formatPercent(data: string | number,
                         applyPercentage: boolean, showAsPercentageOfTotal: boolean): string {
        if(!applyPercentage || showAsPercentageOfTotal) {
            return data.toString();
        }
        if(applyPercentage){
            if(isNaN(Number(data))){
                return data.toString();
            }
        }
        return (Number(data) * 100).toString();
    }

    private getUnitSign(unit: string, maxValue: number = undefined): string {
        const unitSign = this.unitSigns[unit?.toLowerCase()];

        if (unitSign == null) {
            return 'None';
        }

        if (typeof unitSign === 'string') {
            return unitSign;
        }

        return unitSign(maxValue < 0 ? (maxValue * -1) : maxValue );
    }

    // TODO: payam (refact) this is not the best way to change numbers
    private getPersianNumber(value: string): string {
        switch (value) {
            case '0':
                return '۰';
            case '1':
                return '۱';
            case '2':
                return '۲';
            case '3':
                return '۳';
            case '4':
                return '۴';
            case '5':
                return '۵';
            case '6':
                return '۶';
            case '7':
                return '۷';
            case '8':
                return '۸';
            case '9':
                return '۹';
            default:
                return value;
        }
    }
}
